import "./i18n-config"
import { TranslationMessages } from "@repo/common"
import { useMessages, useTranslations } from "use-intl"
import { z } from "zod"

const API_ERROR_KEY_PREFIX = "api.errors."

type ApiErrorMessages = TranslationMessages["api"]["errors"]
type ApiErrorKey = keyof ApiErrorMessages

// The API answers with `{ message: "api.errors.<key>", values?: {...} }`;
// `values` fill in the ICU variables of messages that have any.
const ApiErrorSchema = z.object({
  response: z.object({
    data: z.object({
      message: z.string().min(1),
      values: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .optional(),
    }),
  }),
})

const isApiErrorKey = (
  errors: ApiErrorMessages | undefined,
  key: string,
): key is ApiErrorKey => errors !== undefined && key in errors

/**
 * Returns a function that turns a failed API request (an AxiosError) into
 * text for the user: the translated `api.errors.*` message when the API sent
 * a known key, the raw message for anything else the API sent, and otherwise
 * `fallback` or a generic "something went wrong".
 */
export const useTranslateApiError = () => {
  const t = useTranslations()
  const tApiErrors = useTranslations("api.errors")
  const messages = useMessages()

  return (error: Error | null, fallback?: string): string => {
    const parsed = ApiErrorSchema.safeParse(error)
    if (!parsed.success) return fallback ?? t("ui.errors.generic")

    const { message, values } = parsed.data.response.data
    if (!message.startsWith(API_ERROR_KEY_PREFIX)) return message

    const key = message.slice(API_ERROR_KEY_PREFIX.length)
    if (!isApiErrorKey(messages.api?.errors, key)) return message
    return tApiErrors(key, values)
  }
}
